import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Page, useCurrent, Grid } from "./common";
import { PageHead, StatusBadge, Stat, Seg } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions, money, quoteTotal, outstanding } from "../lib/store";
import { useCan } from "../lib/auth";
import { fmtDate, daysUntil } from "../lib/format";
import { billingTotals } from "../lib/billing";
import QuotationDoc from "./QuotationDoc";
import AgreedFeeDoc from "../components/AgreedFeeDoc";

export default function Quotes() {
  const p = useCurrent();
  const nav = useNavigate();
  const canEdit = useCan("payments");
  const [open, setOpen] = useState<string | null>(null);
  const [view, setView] = useState<"quote" | "ledger">("quote");
  if (!p) return null;

  const owed = outstanding(p);
  const paid = p.invoices.filter((i) => i.status === "paid").reduce((s, i) => s + i.amount, 0);
  const overdue = p.invoices.filter((i) => i.status === "overdue").reduce((s, i) => s + i.amount, 0);
  const accepted = p.quotations.filter((q) => q.status === "accepted").reduce((s, q) => s + quoteTotal(q).total, 0);
  const t = billingTotals(p);
  const ccy = p.billing?.currency ?? p.currency;

  return (
    <Page>
      <PageHead kicker={`${p.name} · Finance`} title="Quotations & payments">
        <Seg value={view} onChange={setView} options={[{ value: "quote", label: "Detailed quotation" }, { value: "ledger", label: "Issued & invoices" }]} />
      </PageHead>

      {/* On a contracted project the quotation ledger is not the source of truth —
          the signed structure is, in its own currency. */}
      <Grid cols="repeat(4,1fr)" style={{ marginBottom: 26 }}>
        <Stat label={t ? "Contract value" : "Accepted quotes"} sub="Contracted value">{money(t ? t.billable : accepted, ccy)}</Stat>
        <Stat label="Paid to date" sub="Received">{money(t ? t.paid : paid, ccy)}</Stat>
        <Stat label="Outstanding" accent sub="Awaiting payment">{money(t ? t.dueNow : owed, ccy)}</Stat>
        <Stat label={t ? "Still to bill" : "Overdue"} sub={t ? "Not yet invoiced" : overdue ? "Action needed" : "None"}>{money(t ? t.remainingToBill : overdue, ccy)}</Stat>
      </Grid>

      {/* Without a configured estimate the quotation would price itself off the
          rate-card minimums and show a fee nobody agreed to — on a project billed
          from a signed contract that figure is just noise, so the agreed
          structure is rendered instead. */}
      {view === "quote" && (p.billing ? <AgreedFeeDoc p={p} /> : p.estimate ? <QuotationDoc p={p} /> : (
        <div className="card pad reveal" style={{ maxWidth: 640 }}>
          <div className="kicker">No quotation on this project</div>
          <p className="muted" style={{ fontSize: 14.5, marginTop: 8, lineHeight: 1.6 }}>
            {p.billing
              ? <>This project is billed from its agreed payment structure, not from a fee estimate.
                  The figures live in <b>Billing</b>.</>
              : <>Configure the scope in the fee estimator and a detailed quotation builds itself here.</>}
          </p>
          <button className="btn accent" style={{ marginTop: 14 }}
            onClick={() => nav(`/studio/${p.id}/${p.billing ? "billing" : "estimator"}`)}>
            {p.billing ? "Open Billing" : "Open the fee estimator"} <Icon.arrow width={15} />
          </button>
        </div>
      ))}


      {view === "ledger" && (
      <Grid cols="1fr 1fr" gap={22}>
        {/* Quotations */}
        <div className="col" style={{ gap: 16 }}>
          <div className="row between reveal"><h3 className="serif" style={{ fontSize: 22 }}>Quotations</h3><button className="btn ghost sm"><Icon.plus width={14} />New quote</button></div>
          {p.quotations.map((q) => {
            const t = quoteTotal(q);
            const isOpen = open === q.id;
            return (
              <div key={q.id} className="card reveal" style={{ overflow: "hidden" }}>
                <button className="row between" onClick={() => setOpen(isOpen ? null : q.id)} style={{ width: "100%", padding: 18, border: "none", background: "transparent", cursor: "pointer", textAlign: "left" }}>
                  <div>
                    <div className="row" style={{ gap: 10 }}><span className="mono dim" style={{ fontSize: 12 }}>{q.ref}</span><StatusBadge status={q.status} /></div>
                    <div className="serif" style={{ fontSize: 18, marginTop: 5 }}>{q.title}</div>
                  </div>
                  <div style={{ textAlign: "right" }}><div className="serif" style={{ fontSize: 20 }}>{money(t.total, p.currency)}</div><div className="dim" style={{ fontSize: 11 }}>{fmtDate(q.issuedOn)}</div></div>
                </button>
                {isOpen && (
                  <div style={{ padding: "0 18px 18px" }}>
                    <div className="panel" style={{ padding: 14 }}>
                      {q.lines.map((l) => (
                        <div key={l.id} className="row between" style={{ padding: "6px 0", fontSize: 13.5 }}>
                          <span>{l.label} <span className="dim">· {l.qty} {l.unit}</span></span>
                          <span className="mono">{money(l.qty * l.rate, p.currency)}</span>
                        </div>
                      ))}
                      <div className="divider" style={{ margin: "10px 0" }} />
                      <div className="row between dim" style={{ fontSize: 12.5 }}><span>Subtotal</span><span className="mono">{money(t.sub, p.currency)}</span></div>
                      <div className="row between dim" style={{ fontSize: 12.5, marginTop: 4 }}><span>Tax ({Math.round(q.taxRate * 100)}%)</span><span className="mono">{money(t.tax, p.currency)}</span></div>
                      <div className="row between" style={{ fontSize: 15, marginTop: 8, fontWeight: 600 }}><span>Total</span><span className="mono">{money(t.total, p.currency)}</span></div>
                    </div>
                    {q.note && <div className="dim" style={{ fontSize: 12.5, marginTop: 10, fontStyle: "italic" }}>{q.note}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Invoices */}
        <div className="col" style={{ gap: 16 }}>
          <h3 className="serif reveal" style={{ fontSize: 22 }}>Invoices & payments</h3>
          <div className="card reveal" style={{ overflow: "hidden" }}>
            {p.invoices.map((inv, i) => {
              const due = daysUntil(inv.dueOn);
              const canPay = inv.status === "due" || inv.status === "overdue";
              return (
                <div key={inv.id} className="row between" style={{ padding: "14px 18px", borderTop: i ? "1px solid var(--line)" : "none", gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="row" style={{ gap: 8 }}><span className="mono dim" style={{ fontSize: 11.5 }}>{inv.ref}</span><StatusBadge status={inv.status} /></div>
                    <div style={{ fontSize: 14, fontWeight: 500, marginTop: 4 }}>{inv.title}</div>
                    <div className="dim" style={{ fontSize: 11.5, marginTop: 2 }}>
                      {inv.status === "paid" ? `Paid ${fmtDate(inv.paidOn)}` : inv.status === "scheduled" ? `Scheduled ${fmtDate(inv.dueOn)}` : `Due ${fmtDate(inv.dueOn)} · ${due < 0 ? `${-due}d overdue` : `in ${due}d`}`}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div className="serif" style={{ fontSize: 18 }}>{money(inv.amount, p.currency)}</div>
                    {canEdit && canPay && <button className="btn soft sm" style={{ marginTop: 6 }} onClick={() => actions.payInvoice(p.id, inv.id)}><Icon.check width={13} />Record</button>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Grid>
      )}
    </Page>
  );
}
